import { CognitoUser, CognitoUserAttribute, CognitoUserPool } from 'amazon-cognito-identity-js';
import axios from 'axios';
import { cognitoPhoneNumber } from '../../../lib/functions/phoneNumber';

function constructUser(cognitoUser, session) {
  return {
    username: cognitoUser.getUsername(),
    tokens: {
      IdToken: session.getIdToken().getJwtToken(),
      AccessToken: session.getAccessToken().getJwtToken(),
      RefreshToken: session.getRefreshToken().getToken(),
    },
    attributes: {},
  };
}

// cannot use ES6 classes, the methods are not enumerable, properties are.
export default function ActionsFactory() {
  const cognitoUserPool = new CognitoUserPool({
    UserPoolId: process.env.VUE_APP_COGNITO_POOL_ID,
    ClientId: process.env.VUE_APP_COGNITO_CLIENT_ID,
    Paranoia: 6,
  });

  return {
    getCurrentUser({ commit }) {
      return new Promise((resolve, reject) => {
        const cognitoUser = cognitoUserPool.getCurrentUser();

        if (!cognitoUser) {
          reject(new Error("Can't retrieve the current user"));
          return;
        }

        cognitoUser.getSession((err, session) => {
          if (err) {
            reject(err);
            return;
          }

          const constructedUser = constructUser(cognitoUser, session);
          // Call AUTHENTICATE because it's utterly the same
          commit('authenticate', constructedUser);
          resolve(constructedUser);
        });
      });
    },

    async authenticateUser({ commit }, payload) {
      const result = await axios.post(`${process.env.VUE_APP_API_ROOT}/auth/signin`, {
        username: payload.username,
        password: payload.password,
        adminInitChangePassword: payload.adminInitChangePassword,
      });
      if (result.data.needsConversionToCognito) {
        commit('convertAccount', true);
        commit('setUser', result.data.user);
      } else {
        commit('authenticate', result.data.cognitoUser);
        commit('setMUser', result.data.mongoUser);
      }
    },
    async signUp({ commit }, userInfo) {
      /* userInfo: { username, password, attributes } */
      userInfo.attributes.phone_number = cognitoPhoneNumber(userInfo.attributes.phone_number);
      const userAttributes = Object.keys(userInfo.attributes || {}).map(
        (key) => new CognitoUserAttribute({
          Name: key,
          Value: userInfo.attributes[key],
        })
      );
      return new Promise((resolve, reject) => {
        cognitoUserPool.signUp(userInfo.username, userInfo.password, userAttributes, null, (err, data) => {
          if (!err) {
            if (userInfo.needsConverting === false) {
              commit('setUser', {
                username: data.user.getUsername(),
                tokens: null, // no session yet
                attributes: {},
              });
            }
            resolve({ userConfirmationNecessary: !data.userConfirmed });
            return;
          }
          reject(err);
        });
      });
    },

    confirmRegistration(_store, payload) {
      const cognitoUser = new CognitoUser({
        Pool: cognitoUserPool,
        Username: payload.username,
      });

      return new Promise((resolve, reject) => {
        cognitoUser.confirmRegistration(payload.code, true, (err) => {
          if (!err) {
            resolve();
            return;
          }
          reject(err);
        });
      });
    },

    resendConfirmationCode(_store, payload) {
      const cognitoUser = new CognitoUser({
        Pool: cognitoUserPool,
        Username: payload.username,
      });

      return new Promise((resolve, reject) => {
        cognitoUser.resendConfirmationCode((err) => {
          if (!err) {
            resolve();
            return;
          }
          reject(err);
        });
      });
    },

    forgotPassword(_store, payload) {
      const cognitoUser = new CognitoUser({
        Pool: cognitoUserPool,
        Username: payload.username,
      });

      return new Promise((resolve, reject) => cognitoUser.forgotPassword({
        onSuccess() {
          resolve();
        },
        onFailure(err) {
          reject(err);
        },
      }));
    },

    confirmPassword(_store, payload) {
      const cognitoUser = new CognitoUser({
        Pool: cognitoUserPool,
        Username: payload.username,
      });

      return new Promise((resolve, reject) => {
        cognitoUser.confirmPassword(payload.code, payload.newPassword, {
          onFailure(err) {
            reject(err);
          },
          onSuccess() {
            resolve();
          },
        });
      });
    },

    // Only for authenticated users
    changePassword({ state }, payload) {
      return new Promise((resolve, reject) => {
        // Make sure the user is authenticated
        if (state.user === null || (state.user && state.user.tokens === null)) {
          reject(new Error('User is unauthenticated'));
          return;
        }
        const cognitoUser = new CognitoUser({
          Pool: cognitoUserPool,
          Username: state.user.username,
        });

        // Restore session without making an additional call to API
        cognitoUser.signInUserSession = cognitoUser.getCognitoUserSession(state.user.tokens);
        cognitoUser.changePassword(payload.oldPassword, payload.newPassword, (err) => {
          if (!err) {
            resolve();
            return;
          }
          reject(err);
        });
      });
    },

    // Only for authenticated users
    updateAttributes({ state }, payload) {
      return new Promise((resolve, reject) => {
        // Make sure the user is authenticated
        if (state.user === null || (state.user && state.user.tokens === null)) {
          reject(new Error('User is unauthenticated'));
          return;
        }

        const cognitoUser = new CognitoUser({
          Pool: cognitoUserPool,
          Username: state.user.username,
        });

        // Restore session without making an additional call to API
        cognitoUser.signInUserSession = cognitoUser.getCognitoUserSession(state.user.tokens);
        const attributes = Object.keys(payload || {}).map(
          (key) => new CognitoUserAttribute({
            Name: key,
            Value: payload[key],
          })
        );

        cognitoUser.updateAttributes(attributes, (err) => {
          if (!err) {
            resolve();
            return;
          }
          reject(err);
        });
      });
    },

    // Only for authenticated users
    getUserAttributes({ commit, state }) {
      return new Promise((resolve, reject) => {
        // Make sure the user is authenticated
        if (state.user === null || (state.user && state.user.tokens === null)) {
          reject(new Error('User is unauthenticated'));
          return;
        }

        const cognitoUser = new CognitoUser({
          Pool: cognitoUserPool,
          Username: state.user.username,
        });

        // Restore session without making an additional call to API
        cognitoUser.signInUserSession = cognitoUser.getCognitoUserSession(state.user.tokens);

        cognitoUser.getUserAttributes((err, attributes) => {
          if (err) {
            reject(err);
            return;
          }

          const attributesMap = (attributes || []).reduce((accum, item) => {
            accum[item.Name] = item.Value;
            return accum;
          }, {});

          commit('attributes', attributesMap);
          resolve(attributesMap);
        });
      });
    },

    // Only for authenticated users
    signOut({ commit, state }) {
      // console.log('======== Normal Signout Action =======');
      return new Promise((resolve, reject) => {
        // Make sure the user is authenticated
        if (state.user === null || (state.user && state.user.tokens === null)) {
          reject(new Error('User is unauthenticated'));
          return;
        }

        const cognitoUser = new CognitoUser({
          Pool: cognitoUserPool,
          Username: state.user.username,
        });

        cognitoUser.signOut();
        commit('signout');
        resolve();
      });
    },

    updateUserRole({ commit }, role) {
      commit('updateUserRole', role);
    },

    updateTokenExpire({ commit }) {
      commit('updateTokenExpiration');
    },

    setRedirectUrl({ commit }, url) {
      commit('setRedirectUrl', url);
    },

    hardSignOut({ commit }) {
      console.log('== Hard Signout occured ==');
      commit('signout');
    },
  };
}
